import type { AchievementRecord, GameState } from './types';
import { airportById } from '../data/airports';

export interface AchievementProgress {
  current: number;
  target: number;
}

export interface AchievementDefinition {
  id: string;
  title: string;
  description: string;
  category: '启航' | '扩张' | '经营' | '资本' | '挑战';
  achieved: (state: GameState) => boolean;
  progress: (state: GameState) => AchievementProgress;
}

const activeRoutes = (state: GameState) => state.routes.filter((route) => route.active).length;
const debt = (state: GameState) => state.loans.reduce((sum, loan) => sum + loan.principalRemaining, 0);

function externalStockHoldings(state: GameState) {
  return Object.entries(state.playerAssets.stockPortfolio ?? {})
    .filter(([, quantity]) => quantity > 0)
    .map(([companyId, quantity]) => ({
      companyId,
      quantity,
      manager: state.aiManagers.find((item) => item.id === companyId),
    }))
    .filter((holding) => holding.manager?.companyListing?.listed);
}

function positiveOperatingProfitWeeks(state: GameState, weeks: number): number {
  return state.reports.slice(-weeks).filter((report) => report.operatingProfit > 0).length;
}

function positiveCashFlowWeeks(state: GameState, weeks: number): number {
  return state.reports.slice(-weeks).filter((report) => report.netCashFlow > 0).length;
}

function currentCashAtWeekEnd(state: GameState, week: number): number {
  const laterCashChanges = state.ledger
    .filter((entry) => entry.week > week)
    .reduce((sum, entry) => sum + entry.cashAmount, 0);
  return state.cash - laterCashChanges;
}

function survivedCashCrisis(state: GameState): boolean {
  const weeks = new Set([...state.reports.map((report) => report.week), ...state.ledger.map((entry) => entry.week)]);
  return [...weeks].some((week) => currentCashAtWeekEnd(state, week) <= 1_000_000 && state.week - week >= 26 && state.cash > 1_000_000);
}

function crossRegionNetwork(state: GameState): boolean {
  const regions = new Set<string>();
  for (const route of state.routes.filter((item) => item.active)) {
    regions.add(airportById(route.originId).region);
    regions.add(airportById(route.destinationId).region);
  }
  return regions.size >= 3;
}

function hasValueHunterPosition(state: GameState): boolean {
  return externalStockHoldings(state).some(({ companyId, quantity, manager }) => {
    const listing = manager?.companyListing;
    if (!listing || quantity <= 0) return false;
    const cost = state.playerAssets.stockPortfolioCost?.[companyId] ?? 0;
    const averageCost = cost / quantity;
    const issuePrice = listing.sharePriceHistory?.[0]?.price ?? listing.sharePrice;
    return issuePrice > 0 && averageCost <= issuePrice * 0.7 && listing.sharePrice > averageCost;
  });
}

export const ACHIEVEMENTS: AchievementDefinition[] = [
  {
    id: 'first-flight',
    title: '首班航班',
    description: '推进一周，让公司的第一班航班完成结算。',
    category: '启航',
    achieved: (state) => state.reports.length >= 1,
    progress: (state) => ({ current: Math.min(1, state.reports.length), target: 1 }),
  },
  {
    id: 'first-profit',
    title: '第一次盈利',
    description: '单周经营利润首次转为正数。',
    category: '启航',
    achieved: (state) => state.reports.some((report) => report.operatingProfit > 0),
    progress: (state) => ({ current: state.reports.some((report) => report.operatingProfit > 0) ? 1 : 0, target: 1 }),
  },
  {
    id: 'fleet-10',
    title: '十架机队',
    description: '拥有或租赁 10 架飞机。',
    category: '扩张',
    achieved: (state) => state.aircraft.length >= 10,
    progress: (state) => ({ current: state.aircraft.length, target: 10 }),
  },
  {
    id: 'fleet-50',
    title: '规模化运营',
    description: '拥有或租赁 50 架飞机。',
    category: '扩张',
    achieved: (state) => state.aircraft.length >= 50,
    progress: (state) => ({ current: state.aircraft.length, target: 50 }),
  },
  {
    id: 'routes-10',
    title: '航线成网',
    description: '同时运营 10 条活跃航线。',
    category: '扩张',
    achieved: (state) => activeRoutes(state) >= 10,
    progress: (state) => ({ current: activeRoutes(state), target: 10 }),
  },
  {
    id: 'hub-5',
    title: '枢纽网络',
    description: '建设 5 座枢纽，形成多中心网络。',
    category: '扩张',
    achieved: (state) => state.hubs.length >= 5,
    progress: (state) => ({ current: state.hubs.length, target: 5 }),
  },
  {
    id: 'million-passengers',
    title: '百万旅客',
    description: '累计服务 100 万名独立旅客。',
    category: '经营',
    achieved: (state) => (state.careerRecords?.totalPassengers ?? 0) >= 1_000_000,
    progress: (state) => ({ current: state.careerRecords?.totalPassengers ?? 0, target: 1_000_000 }),
  },
  {
    id: 'reputation-90',
    title: '口碑长红',
    description: '公司声誉达到 90。',
    category: '经营',
    achieved: (state) => state.reputation >= 90,
    progress: (state) => ({ current: state.reputation, target: 90 }),
  },
  {
    id: 'cash-100m',
    title: '现金充裕',
    description: '现金储备达到 1 亿。',
    category: '经营',
    achieved: (state) => state.cash >= 100_000_000,
    progress: (state) => ({ current: Math.max(0, state.cash), target: 100_000_000 }),
  },
  {
    id: 'ipo',
    title: '敲响钟声',
    description: '完成公司上市，进入公开资本市场。',
    category: '资本',
    achieved: (state) => state.companyListing.listed,
    progress: (state) => ({ current: state.companyListing.listed ? 1 : 0, target: 1 }),
  },
  {
    id: 'multi-stock-holder',
    title: '多元投资者',
    description: '同时持有 3 家其他上市航空公司的股票。',
    category: '资本',
    achieved: (state) => externalStockHoldings(state).length >= 3,
    progress: (state) => ({ current: externalStockHoldings(state).length, target: 3 }),
  },
  {
    id: 'stock-portfolio',
    title: '投资组合',
    description: '同时持有 5 家其他上市航空公司的股票。',
    category: '资本',
    achieved: (state) => externalStockHoldings(state).length >= 5,
    progress: (state) => ({ current: externalStockHoldings(state).length, target: 5 }),
  },
  {
    id: 'controlling-shareholder',
    title: '控股股东',
    description: '持有一家其他上市航空公司 10% 以上的股份。',
    category: '资本',
    achieved: (state) => externalStockHoldings(state).some(({ quantity, manager }) => quantity >= (manager?.companyListing?.totalShares ?? Infinity) * 0.1),
    progress: (state) => ({ current: Math.min(100, Math.max(0, ...externalStockHoldings(state).map(({ quantity, manager }) => (quantity / Math.max(1, manager?.companyListing?.totalShares ?? 1)) * 100))), target: 10 }),
  },
  {
    id: 'dividend-investor',
    title: '股息投资人',
    description: '累计获得 100 万股息收入。',
    category: '资本',
    achieved: (state) => state.ledger.filter((entry) => entry.category === 'dividend').reduce((sum, entry) => sum + Math.max(0, entry.cashAmount), 0) >= 1_000_000,
    progress: (state) => ({ current: state.ledger.filter((entry) => entry.category === 'dividend').reduce((sum, entry) => sum + Math.max(0, entry.cashAmount), 0), target: 1_000_000 }),
  },
  {
    id: 'value-hunter',
    title: '价值猎手',
    description: '以低于上市价 70% 的平均成本持有股票，并实现浮盈。',
    category: '资本',
    achieved: hasValueHunterPosition,
    progress: (state) => ({ current: hasValueHunterPosition(state) ? 1 : 0, target: 1 }),
  },
  {
    id: 'thirteen-week-profit',
    title: '稳定盈利',
    description: '连续 13 周经营利润为正。',
    category: '经营',
    achieved: (state) => state.reports.length >= 13 && positiveOperatingProfitWeeks(state, 13) === 13,
    progress: (state) => ({ current: positiveOperatingProfitWeeks(state, 13), target: 13 }),
  },
  {
    id: 'cross-continent-network',
    title: '跨洲网络',
    description: '活跃航线覆盖至少 3 个区域。',
    category: '扩张',
    achieved: crossRegionNetwork,
    progress: (state) => {
      const regions = new Set(state.routes.filter((route) => route.active).flatMap((route) => [airportById(route.originId).region, airportById(route.destinationId).region]));
      return { current: regions.size, target: 3 };
    },
  },
  {
    id: 'cash-flow-master',
    title: '现金流大师',
    description: '连续 26 周现金净增加为正。',
    category: '经营',
    achieved: (state) => state.reports.length >= 26 && positiveCashFlowWeeks(state, 26) === 26,
    progress: (state) => ({ current: positiveCashFlowWeeks(state, 26), target: 26 }),
  },
  {
    id: 'crisis-survivor',
    title: '危机生存',
    description: '现金低于 100 万后，继续经营至少 26 周并恢复到 100 万以上。',
    category: '挑战',
    achieved: survivedCashCrisis,
    progress: (state) => ({ current: survivedCashCrisis(state) ? 1 : 0, target: 1 }),
  },
  {
    id: 'investment-holding-group',
    title: '投资控股集团',
    description: '自有公司完成上市，同时持有 5 家其他上市航空公司的股票。',
    category: '挑战',
    achieved: (state) => state.companyListing.listed && externalStockHoldings(state).length >= 5,
    progress: (state) => ({ current: state.companyListing.listed ? externalStockHoldings(state).length : 0, target: 5 }),
  },
  {
    id: 'debt-free-year',
    title: '无债一整年',
    description: '经营满 52 周，并保持没有有息贷款。',
    category: '资本',
    achieved: (state) => state.week >= 52 && debt(state) <= 1,
    progress: (state) => ({ current: state.week >= 52 && debt(state) <= 1 ? 1 : 0, target: 1 }),
  },
  {
    id: 'first-victory',
    title: '胜利路线',
    description: '完成任意一条公司胜利路线。',
    category: '挑战',
    achieved: (state) => (state.victories?.length ?? 0) >= 1,
    progress: (state) => ({ current: Math.min(1, state.victories?.length ?? 0), target: 1 }),
  },
  {
    id: 'all-victories',
    title: '全胜生涯',
    description: '完成全部五条公司胜利路线。',
    category: '挑战',
    achieved: (state) => (state.victories?.length ?? 0) >= 5,
    progress: (state) => ({ current: state.victories?.length ?? 0, target: 5 }),
  },
];

export function updateAchievements(state: GameState): GameState {
  const next = structuredClone(state);
  const unlocked = new Set((next.achievements ?? []).map((achievement) => achievement.id));
  const newlyUnlocked: AchievementRecord[] = [];

  for (const achievement of ACHIEVEMENTS) {
    if (unlocked.has(achievement.id) || !achievement.achieved(next)) continue;
    const record = { id: achievement.id, unlockedWeek: next.week };
    newlyUnlocked.push(record);
    unlocked.add(achievement.id);
  }

  if (newlyUnlocked.length) {
    next.achievements = [...(next.achievements ?? []), ...newlyUnlocked];
    next.events = [
      ...(next.events ?? []),
      ...newlyUnlocked.map((record) => {
        const achievement = ACHIEVEMENTS.find((item) => item.id === record.id);
        return {
          id: `event-${next.nextId++}`,
          week: next.week,
          tone: 'positive' as const,
          title: `成就解锁：${achievement?.title ?? record.id}`,
          detail: achievement?.description ?? '新的成就已经记录。',
        };
      }),
    ].slice(-36);
  } else {
    next.achievements ??= [];
  }

  return next;
}
